import loadjs from 'loadjs';
import { request, appInfo, ua, regReadyHook, getReadySuccessHook, getReadyFailedHook, ReadyCallbackType } from '@weapp/utils';

export interface Jsonp {
  [key: string]: any
}

export const jsonp: Jsonp = {}

export interface Result {
  ECODE?: any;
  error?: any;
}

function mapChunksToJsonp(chunks: Jsonp) {
  Object.keys(chunks).forEach(appId => {
    if(!jsonp[appId]) {
      jsonp[appId] = {};
    }
    jsonp[appId].chunks = chunks[appId];
  })
}

/**
 * ecode版本路径加载支持
 * 如果版本号中存在v_ 说明是版本路径 则需要修改文件名拼接
 * @param url 加载路径
 * @param version 版本号
 */
function appendVerionInLoadUrl(url: string, version: string) {
  if (version && version.includes('v_')) {
    const index = url.lastIndexOf("/");
    const filePath = url.substring(0, index);
    const fullFileName = url.substring(index + 1);
    const fileName = fullFileName.substring(0, fullFileName.lastIndexOf("."));
    const fileExt = fullFileName.substring(fullFileName.lastIndexOf(".") + 1);
    return `${filePath}/${fileName}_${version}.${fileExt}`
  } else {
    return `${url}?v=${version}`
  }
}

function toSplitEcodeData(loadData: any) {
  const emptyCodeVersion = '1';
  if (!loadData) return { _initjs: [], _initcss: [], _chunks: null }
  const { initjs = [], initcss = [], chunks = {}, chunksRelease, initPrefix, chunksPrefix } = loadData;

  const initFileName = ua.device === 'PC' ? 'init' : 'init_mobile'
  const _initjs = initjs.filter((url: string) => url.split(',')?.[1] !== emptyCodeVersion).map((js: string) =>
    `${appendVerionInLoadUrl(`${initPrefix}${js.split(',')?.[0] ? js.split(',')?.[0] + '/' : ''}${initFileName}${js.split(',')?.[2] ? '_' + js.split(',')?.[2] : ''}.js`, js.split(',')?.[1])}`)
  const _initcss = initcss.filter((url: string) => url.split(',')?.[1] !== emptyCodeVersion).map((css: string) =>
    `${appendVerionInLoadUrl(`${initPrefix}${css.split(',')?.[0] ? css.split(',')?.[0] + '/' : ''}${initFileName}${css.split(',')?.[2] ? '_' + css.split(',')?.[2] : ''}.css`, css.split(',')?.[1])}`)
  const _chunks: {[_: string]: string[] } = {}

  Object.keys(chunks).forEach(key => {
    const chunk = chunks[key]

    Object.keys(chunk).forEach((chunk_key: string) => {
      if (_chunks[chunk_key]) return
      const tmpArr = [];
      const jsVersion = chunk[chunk_key].split(',')?.[0], cssVersion = chunk[chunk_key].split(',')?.[1];
      if (jsVersion && jsVersion !== emptyCodeVersion) {
        tmpArr.push(`${appendVerionInLoadUrl(`${chunksPrefix}${key}${chunksRelease}${chunk_key}/index.js`, jsVersion)}`)
      }
      if (cssVersion && cssVersion !== emptyCodeVersion) {
        tmpArr.push(`${appendVerionInLoadUrl(`${chunksPrefix}${key}${chunksRelease}${chunk_key}/index.css`, cssVersion)}`)
      }
      if (tmpArr.length > 0) {
        _chunks[chunk_key] = [...tmpArr]  
      }
    })
  })

  return { _initjs, _initcss, _chunks }
}

function transApiData(ecode: any, isLogin: boolean = false, otherParams?:any) {
  if (isLogin) {
    const { _initjs, _initcss, _chunks } = toSplitEcodeData(ecode.load)
    const { _initjs: _initCommonJs, _initcss: _initCommonCss, _chunks: _commonChunks } = toSplitEcodeData(ecode.common)
    let chunksPrefix = ecode?.load?.chunksPrefix || ecode?.common?.chunksPrefix;
    if (chunksPrefix) window.localStorage.setItem('__ecode_chunks_prefix__', chunksPrefix);
    return {
      chunks: { ..._chunks, ..._commonChunks },
      initjs: [ ..._initjs, ..._initCommonJs ],
      initcss: [ ..._initcss, ..._initCommonCss ],
    }
  } else {
    let ecodeData = ecode.load;
    const {isInit,_lastDataLoginStatus} = otherParams||{};
    //如果是退出登录，ip的情况下，缓存是取的登录后的数据，所以要适配缓存数据
    if(isInit && (_lastDataLoginStatus||(ecode.load && ecode.common))){
      ecodeData = ecode.common;
    }
    const { _initjs, _initcss, _chunks } = toSplitEcodeData(ecodeData)
    let { chunksPrefix = '' } = ecodeData||{};
    if (chunksPrefix) window.localStorage.setItem('__ecode_chunks_prefix__', chunksPrefix);
    return { chunks: _chunks, initjs: _initjs, initcss: _initcss }
  }
}

function updateEcodeWidthLocal(ecode: any, isLogin: boolean = false, refresh: boolean = true, initfiles: any) {
  if (Object.prototype.toString.call(ecode?.load) === '[object Object]') {
    let splitEcodeData: any = transApiData(ecode, isLogin)

    if (isLogin) window.localStorage.setItem('__ecode_data__timestamp', String(new Date().getTime()))

    mapChunksToJsonp(splitEcodeData.chunks);
    const _lastDataLoginStatus = window.localStorage.getItem('__ecode_data__status') == 'true'
    const strecode = JSON.stringify(ecode);
    let reload = false;
    //如果本次ecode加载异常，就不要reload，上次可能是正常的
    if (strecode && (ecode?.load?.chunksPrefix || ecode?.common?.chunksPrefix)) {

      let {isUpdate,newDataStr, newDataJSON} = compareEcodeAndUpdateCache({
        cacheData: initfiles.ecode,
        lastIsLogin: _lastDataLoginStatus,
        newData: strecode,
        isLogin,
        needUpdateCache: true,
      });
      window.localStorage.setItem('__ecode_data__status', String(isLogin || ("common" in newDataJSON && "load" in newDataJSON)));
      window.localStorage.setItem('__ecode_data__', newDataStr);
      if (isUpdate) {
        reload = true
      }
    }

    // 配置开启 => refresh 出现差异reload
    // 缓存为空 => 未加载任何ecode代码
    // 上一次加载异常，本次加载正常，可以刷新
    // 前后缓存数据登录状态不一致 => 当前加载了缓存中错误的ecode代码
    if (
      reload
      && (
        refresh
        || !initfiles.ecode
        || initfiles.ecode==='{}'
        || (_lastDataLoginStatus != isLogin)
      )
      // 特殊处理：基础框架中转页 不进行reload处理
      && window.location.href.indexOf('wea_link_keep_ecode_noreload=1') < 0
    ) {
      window.location.reload();
      return;
    }
    // css 晚一点加载，变更不触发页面重载
    // css && loadjs(css.map((p: string) => `${publicUrl}${p}`), '__ecode_initcss__');
  }
}

function printLog(type:string,msg:any,cachedata:any,newData:any){
  if(localStorage.getItem("setecodelog")){
    console.log(type,msg,cachedata,newData);
    localStorage.setItem("ecodelog",JSON.stringify({type,msg,cachedata,newData}))
  }
}

/**
 * 比对ecode缓存并更新缓存
 * 登录前：公共代码为load
 * 登录后：公共代码为common  租户代码为load
 * @param option
 */
function compareEcodeAndUpdateCache(option:{
  cacheData:string|Jsonp;
  lastIsLogin:boolean;
  newData:string|Jsonp;
  isLogin:boolean;
  needUpdateCache?:boolean;
}){
  let {cacheData,lastIsLogin,newData,isLogin,needUpdateCache} = option;

  let {cacheDataStr='', cacheDataJSON={}, newDataStr='', newDataJSON={}} = (()=>{
    let tempData:any = {};

    let analyzeData=(data:any, strKey:string, jsonKey: string)=>{
      let reset=()=>{
        tempData[strKey] = '{}';
        tempData[jsonKey] = {};
      }
      if(typeof data==='string'){
        try {
          tempData[strKey] = data;
          tempData[jsonKey] = JSON.parse(data);
        }catch (e){
          reset()
        }
      }else {
        try {
          tempData[strKey] = JSON.stringify(data||{});
          tempData[jsonKey] = data||{};
        }catch (e){
          reset()
        }
      }
    }
    analyzeData(cacheData,'cacheDataStr','cacheDataJSON')
    analyzeData(newData,'newDataStr','newDataJSON')
    return tempData;
  })();


  let isUpdate = false;//是否有差异
  let isErrorData = false;//异常数据
  if(cacheDataStr===newDataStr){//刷新，或者域名不同的情况，缓存不变
    printLog('1','0',cacheDataStr,newDataStr)
    //不做任何处理
  }else {

    //判断缓存是登录前还是登录后的数据
    //1.两者数据都有，代表是登录后的数据
    if("common" in cacheDataJSON && "load" in cacheDataJSON){
      if(isLogin){//对比租户代码和公共代码
        //对比common和load是否一致
        if(
          JSON.stringify(cacheDataJSON.common)===JSON.stringify(newDataJSON.common)
          && JSON.stringify(cacheDataJSON.load)===JSON.stringify(newDataJSON.load)
        ){
          //缓存无变化不做任何处理，不需要刷新
          isUpdate = false;
          printLog('2','0',cacheDataStr,newDataStr)
        }else{
          //需要更新缓存,整个更新，页面也需要刷新
          isUpdate = true;
          printLog('3','1',cacheDataStr,newDataStr)
        }
      }else{//对比公共代码
        //对比common和接口的load数据是否一致
        if(JSON.stringify(cacheDataJSON.common)===JSON.stringify(newDataJSON.load)){
          //缓存公共ecode没变化，不做任何处理，不需要刷新
          isUpdate = false;
          newDataStr = cacheDataStr;
          newDataJSON = cacheDataJSON;
          printLog('4','0',cacheDataStr,newDataStr)
        }else{//公共代码改变了
          //更新oldCache.common为newCache.load,  页面也需要刷新
          isUpdate = true;
          newDataJSON = {
            ...cacheDataJSON,
            common:newDataJSON.load
          }
          newDataStr = JSON.stringify(newDataJSON)
          printLog('5','1',cacheDataStr,newDataStr)
        }
      }
    }else if("common" in cacheDataJSON){//目前不存在这种情况，登录后有两个，登录前为load或者两个
      if(lastIsLogin){//代表异常数据，缺少登录后的load数据
        if(!("load" in newDataJSON)){
          if(JSON.stringify(cacheDataJSON.common)===JSON.stringify(newDataJSON.common)){
            //缓存未更新无需刷新
            isUpdate = false;
            printLog('6','0',cacheDataStr,newDataStr)
          }else {
            isUpdate = true;
            printLog('7','1',cacheDataStr,newDataStr)
          }
        }else {
          isErrorData = true;
          printLog('8','1',cacheDataStr,newDataStr)
        }
      }else{//代表异常数据，非登录状态不会有common
        isErrorData = true;
        printLog('9','1',cacheDataStr,newDataStr)
      }
    }else if("load" in cacheDataJSON){//代表是登录前的数据
      if(lastIsLogin){//代表异常数据，缺少登录后的common数据
        if(isLogin){
          if(!("common" in newDataJSON)){
            if(JSON.stringify(cacheDataJSON.load)===JSON.stringify(newDataJSON.load)){
              //缓存未更新无需刷新
              isUpdate = false;
              printLog('10','0',cacheDataStr,newDataStr)
            }else {
              isUpdate = true;
              printLog('11','1',cacheDataStr,newDataStr)
            }
          }else {
            isErrorData = true;
            printLog('12','1',cacheDataStr,newDataStr)
          }
        }else {
          isUpdate = true;
          newDataJSON = {
            ...cacheDataJSON,
            common:newDataJSON.load
          }
          newDataStr = JSON.stringify(newDataJSON);
          printLog('13','1',cacheDataStr,newDataStr)
        }
      }else{//代表登录前的数据，正常
        if(isLogin){//登录状态如果缓存只有公共代码，一定要刷新，将缓存更新为登录后的状态数据
          if("load" in newDataJSON){
            isUpdate = true;
            printLog('14','1',cacheDataStr,newDataStr)
          }else {
            if(JSON.stringify(cacheDataJSON.load)===JSON.stringify(newDataJSON.common)){
              //缓存未更新无需刷新
              isUpdate = false;
              printLog('15','0',cacheDataStr,newDataStr)
            }else {
              isUpdate = true;
              printLog('16','1',cacheDataStr,newDataStr)
            }
          }
        }else {
          if(JSON.stringify(cacheDataJSON.load)===JSON.stringify(newDataJSON.load)){
            //公共代码未更新，不作处理
            isUpdate = false;
            printLog('17','0',cacheDataStr,newDataStr)
          }else {
            isUpdate = true;
            printLog('18','1',cacheDataStr,newDataStr)
          }
        }
      }
    }else {
      isUpdate = true;
      printLog('19','1',cacheDataStr,newDataStr)
    }
  }
  if(isErrorData||isUpdate){
    isUpdate = true;
    // needUpdateCache && window.localStorage.setItem('__ecode_data__', newDataStr);
  }
  return {
    isUpdate,newDataStr, newDataJSON
  };
}

function requestEcodeData(initfiles: any, isLogin: boolean = false) {
  request({
    url: `${window.publicUrlapi || window.publicUrl || ""}/${isLogin ? '' : 'p'}api/app/ecode/cache/global`,
    method: 'get',
    params: {
      device: ua.device
    }
  }).then(res => updateEcodeWidthLocal(res?.data?.ecode, isLogin, res?.data?.refresh, initfiles)).catch(e => {
    // json 数据容灾处理
    request({ url: window.TEAMS?.ecodeGlobalData?.global?.globalAddress, method: 'get' }).then(jsonRes => {
      updateEcodeWidthLocal({ load: jsonRes }, false, true, initfiles)
    }).catch(error => console.error(error))

    const initReadyFailed = getReadyFailedHook('@weapp/ecodesdk', 'initReady');
    typeof initReadyFailed === 'function' && initReadyFailed(e);
    console.error('ecode 加载出错：', e);
  });
}

export function ready(handle: ReadyCallbackType) {
  regReadyHook('@weapp/ecodesdk', 'initReady', handle);
}

export function loadAsyncReady() {
  return new Promise((resolve, reject) => {
    const { initjs, initcss } = transApiData(
      JSON.parse(window.localStorage.getItem('__ecode_data__') as string) || {},
      window.ETEAMSID && window.TEAMS
    )

    // 占用 @weapp/ecode 的二级路径变量规则
    const { publicUrl } = appInfo('@weapp/ecode');
    const publicDomain = window['publicDomainfileEcode'] || '';

    const js = (initjs || []) as string[];
    const css = (initcss || []) as string[];

    let js_files = js.map(i => `${publicDomain}${publicUrl}${i}`)
    let css_files = css.map(i => `${publicDomain}${publicUrl}${i}`)
    const loadKey = `__ecode_init__`;

    loadjs([...js_files, ...css_files], loadKey);
    loadjs.ready(loadKey, {
      success: () => {
        resolve({ status: 'success', loadKey })
      },
      error: (err: any) => {
        resolve({ status: 'error', loadKey, err })
      }
    });
  })
}

/**
 * 判断是否需要加载ecode
 */
function checkIsLoadEcode(){
  const winHref = window.location.href;
  let load = winHref.indexOf("wea_link_keep_disable_ecode")<0;
  if(load && localStorage){
    const disableEcodeUrlStr = localStorage.getItem("disableEcodeUrls");
    try {
      if(disableEcodeUrlStr){
        const urls = JSON.parse(disableEcodeUrlStr);
        if(urls.length){
          urls.forEach((url:string)=>{
            if(load && url && winHref.indexOf(url)>=0){
              load = false;
            }
          })
        }
      }
    }catch (e){}
  }
  return load;
}

export function init() {
  if(!checkIsLoadEcode()) return;
  // 占用 @weapp/ecode 的二级路径变量规则
  const { publicUrl } = appInfo('@weapp/ecode');
  const publicDomain = window['publicDomainfileEcode'] || '';
  const _lastDataLoginStatus = window.localStorage.getItem('__ecode_data__status') == 'true'
  const isLogin = Boolean(window.ETEAMSID && window.TEAMS)

  let ecodeDataStr = window.localStorage.getItem('__ecode_data__');

  // 当前无缓存 必执行接口请求
  if (!ecodeDataStr) {
    requestEcodeData({}, isLogin)
    return
  }
  let ecodeData:any = {};
  try{
    ecodeData = JSON.parse(ecodeDataStr)
  }catch (e){

  }
  //缓存异常情况处理：兼容历史获取失败也写入缓存的情况
  if(!(ecodeData?.load?.chunksPrefix || ecodeData?.common?.chunksPrefix)){
    ecodeData = {};
    ecodeDataStr = JSON.stringify(ecodeData);
    localStorage.setItem("__ecode_data__",'');
  }
  const {
    chunks,
    initjs,
    initcss
  } = transApiData(
    ecodeData,
    window.ETEAMSID && window.TEAMS,
    {isInit:true,_lastDataLoginStatus}
  )

  const initfiles = {
    ecode: ecodeDataStr,
    js: initjs,
    css: initcss,
    chunks: chunks,
  };


  mapChunksToJsonp(initfiles.chunks || {});
  const css = (initfiles.css || []) as string[];
  const js = (initfiles.js || []) as string[];

  if (!(window.weaverSDK?.isExternSystem?.())) {
    js.forEach((p: string )=> window.document.write(`<script src="${publicDomain + publicUrl + p}"></script>`));
    css.forEach((p: string )=> window.document.write(`<link rel="stylesheet" href="${publicDomain + publicUrl + p}" />`));
  }

  if (isLogin) {
    window.TEAMS?.ready?.('ecodeGlobalData', {
      success: function () {
        window.ECODE = window.TEAMS?.ecodeGlobalData?.global;

        const initReadySuccess = getReadySuccessHook('@weapp/ecodesdk', 'initReady');
        typeof initReadySuccess === 'function' && initReadySuccess(window.ECODE);

        // 开启缓存过期时间配置 => 校验缓存是否有效
        if (window.ECODE?.cacheTimeout > 0) {
          const nowTimestamp = new Date().getTime()
          const lastTimestamp = Number(window.localStorage.getItem('__ecode_data__timestamp')) || 0

          if (nowTimestamp - Number(lastTimestamp) >= Number(window.ECODE?.cacheTimeout)) {
            requestEcodeData(initfiles, isLogin)
          }

          return false;
        }

        requestEcodeData(initfiles, isLogin)
      },
      failed: function (error: any) {
        const initReadySuccess = getReadySuccessHook('@weapp/ecodesdk', 'initReady');
        typeof initReadySuccess === 'function' && initReadySuccess(window.ECODE);

        requestEcodeData(initfiles, isLogin)
        window.console?.error?.('ecodeGlobalData ready failed：' + error);
      },
    });
  } else {
    requestEcodeData(initfiles, isLogin)
  }
}
